/*
 * signature-pad.js — shared signature-drawing engine.  window.SignaturePad
 *
 * Three ways to make a signature, one canvas:
 *   - draw   : freehand, pressure/speed-variable stroke width, quadratic smoothing
 *   - type   : a name rendered in a handwriting font
 *   - upload : a photo of a paper signature, background removed by luminance threshold
 *
 * Engine ported from the inline implementation in signature-creator.blade.php
 * (the canonical home going forward — signature-creator will be retrofitted onto
 * this module in a follow-up). The one capability added here is exportPngBytes(),
 * which /sign-pdf needs to feed pdf-lib's embedPng().
 *
 *   var pad = SignaturePad.create({ canvas, host, onChange });
 *   pad.setMode('draw'); ... pad.exportPngBytes().then(bytes => ...);
 *   pad.destroy();
 *
 * SignaturePad.computeOpaqueBounds(imageData, threshold) is exported standalone
 * (pure, no canvas) so the auto-crop logic can be node-unit-tested.
 */
(function (root) {
    'use strict';

    /*
     * computeOpaqueBounds(imageData, threshold=5) -> {top,left,right,bottom}|null
     * Tightest box around pixels whose alpha exceeds `threshold`. Pure: takes a
     * plain {data,width,height} so it runs under node with no canvas.
     */
    function computeOpaqueBounds(imageData, threshold) {
        var thr = (threshold == null) ? 5 : threshold;
        var d = imageData.data, W = imageData.width, H = imageData.height;
        var top = H, left = W, bottom = -1, right = -1;
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                if (d[(y * W + x) * 4 + 3] > thr) {
                    if (y < top) top = y;
                    if (y > bottom) bottom = y;
                    if (x < left) left = x;
                    if (x > right) right = x;
                }
            }
        }
        if (bottom < top || right < left) return null;
        return { top: top, left: left, right: right, bottom: bottom };
    }

    function create(opts) {
        opts = opts || {};
        var canvas = opts.canvas;
        var host = opts.host || canvas.parentElement;
        var ctx = canvas.getContext('2d');
        var fontLoader = typeof opts.fontLoader === 'function' ? opts.fontLoader : null;
        var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};

        var S = {
            mode: 'draw',
            color: opts.color || '#0b1f3a',
            penSize: opts.penSize || 3,
            smoothing: opts.smoothing == null ? 55 : opts.smoothing,
            inkMode: !!opts.inkMode,
            strokes: [],
            redoStack: [],
            drawing: false,
            currentStroke: null,
            lastT: 0, lastX: 0, lastY: 0,
            typedText: '',
            typedFont: opts.defaultFont || 'Dancing Script',
            uploadOriginal: null,   // HTMLImageElement
            uploadProcessed: null,  // HTMLCanvasElement
            threshold: 130, brightness: 0, contrast: 0, invert: false,
            guideLine: opts.guideLine !== false,
            dpr: Math.min(window.devicePixelRatio || 1, 3),
            destroyed: false
        };

        /* ── sizing ──────────────────────────────────────────────── */
        function resize() {
            if (S.destroyed) return;
            var r = host.getBoundingClientRect();
            var w = Math.max(1, Math.round(r.width));
            var h = Math.max(1, Math.round(r.height));
            canvas.width = w * S.dpr;
            canvas.height = h * S.dpr;
            canvas.style.width = w + 'px';
            canvas.style.height = h + 'px';
            ctx.setTransform(S.dpr, 0, 0, S.dpr, 0, 0);
            redraw();
        }
        var resizeTimer;
        function onHostResize() {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(resize, 60);
        }
        /* Safari до 13.1 наблюдателя не знает: голый конструктор ронял create(),
           и окно подписи в /sign-pdf и /pdf-editor открывалось с мёртвым холстом.
           Без него холст следует за окном; при открытии окна вызывающий сам
           зовёт resize(). */
        var ro = null;
        if (typeof ResizeObserver === 'function') {
            ro = new ResizeObserver(onHostResize);
            ro.observe(host);
        } else {
            window.addEventListener('resize', onHostResize);
        }

        /* ── draw engine ─────────────────────────────────────────────
           A stroke is just {color, points:[{x,y}]} — width is NOT baked in
           per point. drawStroke() renders the whole stroke as ONE continuous
           midpoint-quadratic path at the current S.penSize. That is what makes
           the line smooth (no per-segment gaps — the old per-segment path left
           undrawn slivers that read as a dashed line) AND lets the thickness
           slider re-render an already-drawn signature at a new width. */
        function pos(e) {
            var r = canvas.getBoundingClientRect();
            return { x: e.clientX - r.left, y: e.clientY - r.top };
        }
        function onDown(e) {
            if (S.mode !== 'draw') return;
            e.preventDefault();
            try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
            S.drawing = true;
            var p = pos(e);
            /* t (timestamp) is stored on every point — the ink renderer reads
               it to derive pen speed; the smooth renderer ignores it */
            S.currentStroke = { color: S.color, points: [{ x: p.x, y: p.y, t: e.timeStamp || performance.now() }] };
        }
        function onMove(e) {
            if (!S.drawing || S.mode !== 'draw') return;
            e.preventDefault();
            /* coalesced events fill the gaps between pointer ticks on fast
               strokes — denser points, visibly smoother curve */
            var evs = (e.getCoalescedEvents && e.getCoalescedEvents()) || [e];
            if (!evs.length) evs = [e];
            for (var i = 0; i < evs.length; i++) {
                var p = pos(evs[i]);
                S.currentStroke.points.push({ x: p.x, y: p.y, t: evs[i].timeStamp || performance.now() });
            }
            redraw();
            drawStroke(S.currentStroke);
        }
        function onUp() {
            if (!S.drawing) return;
            S.drawing = false;
            if (S.currentStroke && S.currentStroke.points.length > 1) {
                S.strokes.push(S.currentStroke);
                S.redoStack = [];
            }
            S.currentStroke = null;
            redraw();
            onChange();
        }
        canvas.addEventListener('pointerdown', onDown);
        canvas.addEventListener('pointermove', onMove);
        canvas.addEventListener('pointerup', onUp);
        canvas.addEventListener('pointercancel', onUp);

        /* drawStroke dispatches on S.inkMode so a toggle re-renders the whole
           signature in either style — a signature is one consistent look. */
        function drawStroke(stroke) {
            if (S.inkMode) drawStrokeInk(stroke);
            else drawStrokeSmooth(stroke);
        }

        /* even-width pen — one continuous midpoint-quadratic path */
        function drawStrokeSmooth(stroke) {
            var pts = stroke.points;
            if (pts.length < 2) return;
            ctx.strokeStyle = stroke.color;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = S.penSize;
            ctx.beginPath();
            ctx.moveTo(pts[0].x, pts[0].y);
            /* quadratic through each point, ending each segment at the midpoint
               to the next — the next segment starts at that same midpoint, so
               the path is unbroken */
            for (var i = 1; i < pts.length - 1; i++) {
                ctx.quadraticCurveTo(
                    pts[i].x, pts[i].y,
                    (pts[i].x + pts[i + 1].x) / 2,
                    (pts[i].y + pts[i + 1].y) / 2);
            }
            ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
            ctx.stroke();
        }

        /* ink pen — width tracks pen speed (slow = thick, fast = thin) like a
           real fountain pen. Rendered as a dense run of overlapping filled
           circles: gapless by construction, no self-intersection artifacts on
           sharp turns (the etalon-robust way to do variable-width strokes). */
        function inkRadius(speed) {
            var base = S.penSize / 2;
            var sp = Math.min(speed, 2.2) / 2.2;            // 0 (slow) .. 1 (fast)
            return base * (1.55 - sp * 1.05);               // 1.55×base .. 0.5×base
        }
        function stampCircle(x, y, r) {
            ctx.beginPath();
            ctx.arc(x, y, Math.max(0.4, r), 0, Math.PI * 2);
            ctx.fill();
        }
        function drawStrokeInk(stroke) {
            var pts = stroke.points;
            if (!pts.length) return;
            ctx.fillStyle = stroke.color;
            if (pts.length === 1) { stampCircle(pts[0].x, pts[0].y, inkRadius(0)); return; }
            var prevR = inkRadius(0);
            stampCircle(pts[0].x, pts[0].y, prevR);          // pen-down dot
            for (var i = 1; i < pts.length; i++) {
                var a = pts[i - 1], b = pts[i];
                var dx = b.x - a.x, dy = b.y - a.y;
                var dist = Math.sqrt(dx * dx + dy * dy);
                var dt = Math.max(1, (b.t || 0) - (a.t || 0));
                var r = inkRadius(dist / dt);
                var steps = Math.max(1, Math.ceil(dist / Math.max(1, r * 0.4)));
                for (var s = 1; s <= steps; s++) {
                    var f = s / steps;
                    stampCircle(a.x + dx * f, a.y + dy * f, prevR + (r - prevR) * f);
                }
                prevR = r;
            }
        }

        function cssW() { return canvas.width / S.dpr; }
        function cssH() { return canvas.height / S.dpr; }

        function drawGuide() {
            if (!S.guideLine || S.mode !== 'draw') return;
            var w = cssW(), h = cssH(), y = h * 0.72;
            ctx.save();
            ctx.setLineDash([6, 4]);
            ctx.strokeStyle = 'rgba(140,140,160,.30)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(w * 0.08, y);
            ctx.lineTo(w * 0.92, y);
            ctx.stroke();
            ctx.restore();
        }

        function renderTyped() {
            var text = S.typedText || '';
            if (!text) return;
            var w = cssW(), h = cssH();
            ctx.fillStyle = S.color;
            var size = Math.min(h * 0.55, 90);
            ctx.font = size + 'px "' + S.typedFont + '", cursive';
            var m = ctx.measureText(text);
            while (m.width > w * 0.88 && size > 12) {
                size -= 2;
                ctx.font = size + 'px "' + S.typedFont + '", cursive';
                m = ctx.measureText(text);
            }
            ctx.fillText(text, (w - m.width) / 2, h * 0.62);
            awaitFace(text);
        }

        /* Холст рисует шрифтом, который УЖЕ загружен, и сам себя не
           перерисовывает, когда начертание приходит: выбранный в списке
           «Great Vibes» показывался запасным курсивом до следующей буквы, а
           «Использовать» сразу после выбора клало в PDF чужой шрифт (16.10.2026,
           вместе с переездом шрифтов подписи на свой origin — public/fonts/
           sig-type/). Начертание не готово — грузим его под ЭТОТ текст
           (unicode-range: у кириллицы свой файл) и рисуем заново; одна попытка
           на пару «шрифт + текст», так что недоступный файл не крутит цикл. */
        /* ⚠️ Не по document.fonts.check(): WebKit, пока начертание грузится по
           запросу холста, на миг ставит ему `error`, а check() на `error`
           отвечает «готово» — подпись оставалась запасным курсивом (замер
           16.10, WebKit, /sign-pdf: 5 из 5 латинских шрифтов). Поэтому: сами
           просим load() и перерисовываем по его ответу И по `loadingdone`
           набора шрифтов (WebKit доводит начертание до `loaded` позже). */
        var faceTried = {};
        function faceSpec() { return '48px "' + S.typedFont + '"'; }
        function awaitFace(text) {
            var key = S.typedFont + '|' + text;
            if (faceTried[key]) return;
            faceTried[key] = 1;
            faceReady(text).then(function () { if (!S.destroyed && S.mode === 'type') redraw(); });
        }
        /* Готов ли шрифт напечатанной подписи — не дольше 3 с: выгрузка без
           шрифта лучше, чем кнопка, которая не отвечает. */
        function faceReady(text) {
            if (S.mode !== 'type' || !text) return Promise.resolve();
            try {
                if (!document.fonts || !document.fonts.load) return Promise.resolve();
                var p = document.fonts.load(faceSpec(), text).then(function () {}, function () {});
                return Promise.race([p, new Promise(function (r) { setTimeout(r, 3000); })]);
            } catch (e) { return Promise.resolve(); }
        }
        function onFacesDone() { if (!S.destroyed && S.mode === 'type' && S.typedText) redraw(); }
        try { if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', onFacesDone); } catch (e) {}

        function renderUpload() {
            if (!S.uploadProcessed) return;
            var w = cssW(), h = cssH();
            var iw = S.uploadProcessed.width, ih = S.uploadProcessed.height;
            var scale = Math.min((w * 0.92) / iw, (h * 0.92) / ih, 1);
            var dw = iw * scale, dh = ih * scale;
            ctx.drawImage(S.uploadProcessed, (w - dw) / 2, (h - dh) / 2, dw, dh);
        }

        function redraw() {
            ctx.clearRect(0, 0, cssW(), cssH());
            if (S.mode === 'draw') {
                drawGuide();
                S.strokes.forEach(drawStroke);
            } else if (S.mode === 'type') {
                renderTyped();
            } else if (S.mode === 'upload') {
                renderUpload();
            }
        }

        /* ── upload processing ───────────────────────────────────── */
        function processUpload() {
            if (!S.uploadOriginal) { S.uploadProcessed = null; return; }
            var img = S.uploadOriginal;
            var tmp = document.createElement('canvas');
            tmp.width = img.naturalWidth || img.width;
            tmp.height = img.naturalHeight || img.height;
            var tc = tmp.getContext('2d');
            tc.filter = 'brightness(' + (100 + S.brightness) + '%) contrast(' + (100 + S.contrast) + '%)';
            tc.drawImage(img, 0, 0);
            tc.filter = 'none';

            var id = tc.getImageData(0, 0, tmp.width, tmp.height);
            var d = id.data;
            for (var i = 0; i < d.length; i += 4) {
                var gray = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
                var ink = S.invert ? gray > S.threshold : gray < S.threshold;
                if (ink) {
                    d[i] = d[i + 1] = d[i + 2] = 0;
                    d[i + 3] = Math.round(255 * (S.invert ? gray / 255 : 1 - gray / 255));
                } else {
                    d[i + 3] = 0;
                }
            }
            tc.putImageData(id, 0, 0);

            if (S.color !== '#000000') {
                tc.globalCompositeOperation = 'source-in';
                tc.fillStyle = S.color;
                tc.fillRect(0, 0, tmp.width, tmp.height);
                tc.globalCompositeOperation = 'source-over';
            }

            /* auto-crop to the inked region */
            var bounds = computeOpaqueBounds(tc.getImageData(0, 0, tmp.width, tmp.height), 10);
            if (bounds) {
                var pad = 4;
                var l = Math.max(0, bounds.left - pad), t = Math.max(0, bounds.top - pad);
                var r = Math.min(tmp.width - 1, bounds.right + pad);
                var b = Math.min(tmp.height - 1, bounds.bottom + pad);
                var cw = r - l + 1, ch = b - t + 1;
                var cropped = document.createElement('canvas');
                cropped.width = cw; cropped.height = ch;
                cropped.getContext('2d').drawImage(tmp, l, t, cw, ch, 0, 0, cw, ch);
                S.uploadProcessed = cropped;
            } else {
                S.uploadProcessed = tmp;
            }
        }

        function loadUploadImage(file) {
            return new Promise(function (resolve, reject) {
                var url = URL.createObjectURL(file);
                var img = new Image();
                img.onload = function () {
                    URL.revokeObjectURL(url);
                    S.uploadOriginal = img;
                    processUpload();
                    redraw();
                    onChange();
                    resolve();
                };
                img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('image_decode_failed')); };
                img.src = url;
            });
        }

        /* ── export ──────────────────────────────────────────────── */
        function getExportCanvas() {
            if (isEmpty()) return null;
            var snap = document.createElement('canvas');
            snap.width = canvas.width;
            snap.height = canvas.height;
            var sc = snap.getContext('2d');
            /* re-render WITHOUT the guide line so it never leaks into output */
            var savedGuide = S.guideLine;
            S.guideLine = false;
            redraw();
            sc.drawImage(canvas, 0, 0);
            S.guideLine = savedGuide;
            redraw();

            var bounds = computeOpaqueBounds(sc.getImageData(0, 0, snap.width, snap.height), 5);
            if (!bounds) return null;
            var pad = Math.round(8 * S.dpr);
            var l = Math.max(0, bounds.left - pad), t = Math.max(0, bounds.top - pad);
            var r = Math.min(snap.width - 1, bounds.right + pad);
            var b = Math.min(snap.height - 1, bounds.bottom + pad);
            var cw = r - l + 1, ch = b - t + 1;

            /* upscale 2× so the embedded signature stays crisp in the PDF */
            var out = document.createElement('canvas');
            out.width = cw * 2;
            out.height = ch * 2;
            out.getContext('2d').drawImage(snap, l, t, cw, ch, 0, 0, out.width, out.height);
            return out;
        }

        function exportBlob(mime, quality) {
            return faceReady(S.typedText).then(function () { return new Promise(function (resolve, reject) {
                var c = getExportCanvas();
                if (!c) { reject(new Error('empty_signature')); return; }
                c.toBlob(function (blob) {
                    blob ? resolve(blob) : reject(new Error('export_failed'));
                }, mime || 'image/png', quality);
            }); });
        }

        function exportPngBytes() {
            return exportBlob('image/png').then(function (blob) {
                return blob.arrayBuffer();
            }).then(function (buf) {
                return new Uint8Array(buf);
            });
        }

        /* ── state ───────────────────────────────────────────────── */
        function isEmpty() {
            if (S.mode === 'draw') return S.strokes.length === 0;
            if (S.mode === 'type') return !S.typedText.trim();
            if (S.mode === 'upload') return !S.uploadProcessed;
            return true;
        }

        function setMode(m) {
            if (m !== 'draw' && m !== 'type' && m !== 'upload') return inst;
            S.mode = m;
            redraw();
            onChange();
            return inst;
        }

        function setOption(key, val) {
            switch (key) {
                case 'color':
                    /* a signature is one ink colour — recolour every existing
                       stroke, not just future ones */
                    S.color = val;
                    S.strokes.forEach(function (s) { s.color = val; });
                    if (S.currentStroke) S.currentStroke.color = val;
                    if (S.mode === 'upload') processUpload();
                    break;
                case 'penSize': S.penSize = +val; break;
                case 'inkMode': S.inkMode = !!val; break;
                case 'smoothing': S.smoothing = +val; break;
                case 'guideLine': S.guideLine = !!val; break;
                case 'typedText': S.typedText = String(val); break;
                case 'typedFont':
                    S.typedFont = String(val);
                    if (fontLoader && S.mode === 'type') {
                        fontLoader(S.typedFont).then(redraw).catch(function () { redraw(); });
                        return inst;
                    }
                    break;
                case 'threshold': S.threshold = +val; processUpload(); break;
                case 'brightness': S.brightness = +val; processUpload(); break;
                case 'contrast': S.contrast = +val; processUpload(); break;
                case 'invert': S.invert = !!val; processUpload(); break;
                default: return inst;
            }
            redraw();
            onChange();
            return inst;
        }

        function undo() {
            if (S.mode === 'draw' && S.strokes.length) {
                S.redoStack.push(S.strokes.pop());
                redraw(); onChange();
            }
            return inst;
        }
        function redo() {
            if (S.mode === 'draw' && S.redoStack.length) {
                S.strokes.push(S.redoStack.pop());
                redraw(); onChange();
            }
            return inst;
        }
        function clear() {
            S.strokes = []; S.redoStack = [];
            S.typedText = '';
            S.uploadOriginal = null; S.uploadProcessed = null;
            redraw(); onChange();
            return inst;
        }

        function destroy() {
            S.destroyed = true;
            try { if (document.fonts && document.fonts.removeEventListener) document.fonts.removeEventListener('loadingdone', onFacesDone); } catch (e) {}
            if (ro) { try { ro.disconnect(); } catch (_) {} }
            else window.removeEventListener('resize', onHostResize);
            canvas.removeEventListener('pointerdown', onDown);
            canvas.removeEventListener('pointermove', onMove);
            canvas.removeEventListener('pointerup', onUp);
            canvas.removeEventListener('pointercancel', onUp);
        }

        var inst = {
            setMode: setMode,
            setOption: setOption,
            undo: undo, redo: redo, clear: clear,
            canUndo: function () { return S.mode === 'draw' && S.strokes.length > 0; },
            canRedo: function () { return S.mode === 'draw' && S.redoStack.length > 0; },
            isEmpty: isEmpty,
            getMode: function () { return S.mode; },
            loadUploadImage: loadUploadImage,
            getExportCanvas: getExportCanvas,
            exportBlob: exportBlob,
            exportPngBytes: exportPngBytes,
            resize: resize,
            destroy: destroy
        };

        /* initial sizing — defer one frame so host has layout */
        if (host.getBoundingClientRect().width > 0) resize();
        else requestAnimationFrame(resize);

        return inst;
    }

    root.SignaturePad = {
        create: create,
        computeOpaqueBounds: computeOpaqueBounds
    };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = root.SignaturePad;
    }
})(typeof window !== 'undefined' ? window : globalThis);
